import { Injectable } from '@angular/core';
import { IPSData } from '../models/ips-data';
import { Sensor } from '../models/sensor';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import { GlobalVars } from '../global-vars';
import { fromLonLat } from 'ol/proj';
import proj4 from 'proj4';
import { faSquareFull, faCircle, faPlay } from '@fortawesome/free-solid-svg-icons';
import { faCircle as faCircleSmooth } from '@fortawesome/free-regular-svg-icons';


@Injectable({
  providedIn: 'root'
})
export class SymbologyService {

  getIconClass(sensor: IPSData): string {
    switch (sensor.tendance) {
      case 'hausse':
        return 'pi pi-arrow-up';
      case 'baisse':
        return 'pi pi-arrow-down';
      case 'stable':
        return 'pi pi-equals';
      default:
        return 'pi pi-question'; // Default icon if needed
    }
  }

  // Retourne une icône spécifique en fonction du type de couche que l'on souhaite afficher
  getSensorIconClass(layerType: string): string {
    switch (layerType) {
      case 'pluviometres':
        return 'pi pi-sort-up-fill';
      case 'hydrometrie':
        return 'pi pi-stop';
      default:
        return 'pi pi-times';
    }
  }

  isIPSData(sensor: IPSData | Sensor): sensor is IPSData {
    return (sensor as IPSData).geometry !== undefined;
  }

  createIconElement(tendency: number | null | undefined): HTMLElement {
    const iconElement = document.createElement('div');
    switch (tendency) {
      case -1:
        iconElement.className = 'pi pi-arrow-down';
        break;
      case 0:
        iconElement.className = 'pi pi-equals';
        break;
      case 1:
        iconElement.className = 'pi pi-arrow-up';
        break;
      default:
        iconElement.className = 'pi pi-question'; // Default icon if needed
    }
    iconElement.classList.add("sector-icon");
    return iconElement;
  }
  

  setSectorLayerStyle(layerType: any) {
    return (feature: any) => {
      const fillColor = feature.get('color') || 'white';
      const status = feature.get('status');
      const r = parseInt(fillColor.slice(1, 3), 16);
      const g = parseInt(fillColor.slice(3, 5), 16);
      const b = parseInt(fillColor.slice(5, 7), 16);
      const alpha = (layerType === GlobalVars.sectorLayersType.detailed) ? 0.4 : 1;
      const rgbaColor = [r, g, b, alpha];
      return new Style({
        fill: new Fill({
          color: rgbaColor,
        })
      });
    };
  }

  // Method to get coordinates and icon for IPSData
  processIPSData(sensor: IPSData, zoom: number) {
    const coordinates = fromLonLat(sensor.geometry.geometry.coordinates);
    const icon = this.getIconClass(sensor);
    const fontSize = !sensor.modelId ? '0.8rem' : zoom <= GlobalVars.wfsSimplifiedMaxZoomAndSensors ? '0.6rem' : '1.2rem';
    const textColor = 'white';
    const backgroundColor = sensor.couleur || 'blue';
    const border = !sensor.modelId ? 'none' : zoom <= GlobalVars.wfsSimplifiedMaxZoomAndSensors ? '4px solid white' : '6px solid white';
    const cursor = 'pointer';
    const padding = !sensor.modelId ? '8px' : zoom <= GlobalVars.wfsSimplifiedMaxZoomAndSensors ? '4px' : '8px';

    return { coordinates, icon, fontSize, textColor, backgroundColor, border, cursor, padding, transform:'' };
  }

  // Method to get coordinates and icon for Hydrometrie
  processHydrometrie(sensor: any) {
    const proj_l93 = GlobalVars.projL93;
    const proj_wgs84 = GlobalVars.projWgs84;
    const coords_wgs84 = proj4(proj_l93, proj_wgs84, [sensor.coordonnee_x_station, sensor.coordonnee_y_station]);
    const coordinates = fromLonLat(coords_wgs84);
    const icon = faSquareFull;

    return { coordinates, icon, fontSize: '1rem', textColor: 'black', backgroundColor: 'none', border: 'none', cursor: 'auto', padding:'8px', transform: 'rotate(-45deg);' };
  }

  // Method to get coordinates and icon for generic Sensor
  processPluvio(sensor: Sensor) {
    const coordinates = fromLonLat(sensor.coords.geometry.coordinates);
    const icon = faPlay;

    return { coordinates, icon, fontSize: '1rem', textColor: 'white', backgroundColor: 'none', border: 'none', cursor: 'auto', padding: '8px', transform: 'rotate(-90deg);' };
  }

  // Method to get coordinates and icon for generic Sensor
  processGenericSensor(sensor: Sensor, layerType: string) {
    const coordinates = fromLonLat(sensor.coords.geometry.coordinates);
    const icon = this.getSensorIconClass(layerType);

    return { coordinates, icon, fontSize: '1rem', textColor: 'white', backgroundColor: 'none', border: 'none', cursor: 'auto', padding: '8px', transform: '' };
  }
}
